import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { ProductCard } from "@/components/ProductCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Filter, X } from "lucide-react";

export default function ShopPage() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: category || "",
    newOnly: false,
    featuredOnly: false,
    sort: "newest"
  });

  useEffect(() => {
    setFilters(prev => ({ ...prev, category: category || "" }));
  }, [category]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let url = `${API}/products`;
        const params = new URLSearchParams();
        
        if (filters.category) params.append("category", filters.category);
        if (filters.newOnly) params.append("is_new", "true");
        if (filters.featuredOnly) params.append("is_featured", "true");
        
        if (params.toString()) url += `?${params.toString()}`;
        
        const response = await axios.get(url);
        let data = response.data;
        
        // Client-side sorting
        if (filters.sort === "price-low") {
          data.sort((a, b) => a.price - b.price);
        } else if (filters.sort === "price-high") {
          data.sort((a, b) => b.price - a.price);
        } else if (filters.sort === "newest") {
          data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        }
        
        setProducts(data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [filters]);

  const categoryTitle = {
    clothing: "Clothing",
    accessories: "Accessories",
    apparel: "Apparel"
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 lg:px-8" data-testid="shop-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span className="label-text text-[#39FF14] text-sm tracking-[0.3em]">
              {category ? categoryTitle[category]?.toUpperCase() || "SHOP" : "ALL PRODUCTS"}
            </span>
            <h1 className="mt-2 text-4xl sm:text-5xl heading text-[#EDEDED]">
              {category ? categoryTitle[category] || "Shop" : "Shop All"}
            </h1>
            <p className="mt-2 text-[#888]">{products.length} products</p>
          </div>

          <div className="flex items-center gap-4">
            {/* Mobile Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center gap-2 px-4 py-2 border border-[#333] text-[#EDEDED]"
              data-testid="filter-toggle"
            >
              <Filter size={18} />
              <span className="label-text">Filters</span>
            </button>

            {/* Sort */}
            <Select
              value={filters.sort}
              onValueChange={(value) => setFilters(prev => ({ ...prev, sort: value }))}
            >
              <SelectTrigger className="w-48 bg-transparent border-[#333] text-[#EDEDED]" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-[#333]">
                <SelectItem value="newest" className="text-[#EDEDED] focus:bg-[#1a1a1a] focus:text-[#39FF14]">
                  Newest
                </SelectItem>
                <SelectItem value="price-low" className="text-[#EDEDED] focus:bg-[#1a1a1a] focus:text-[#39FF14]">
                  Price: Low to High
                </SelectItem>
                <SelectItem value="price-high" className="text-[#EDEDED] focus:bg-[#1a1a1a] focus:text-[#39FF14]">
                  Price: High to Low
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Sidebar Filters - Desktop */}
          <aside className={`
            fixed md:relative inset-0 md:inset-auto z-40 md:z-auto
            w-full md:w-64 bg-[#030303] md:bg-transparent
            transform ${showFilters ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
            transition-transform duration-300 p-6 md:p-0
          `}>
            <div className="flex items-center justify-between md:hidden mb-6">
              <h3 className="text-xl heading text-[#EDEDED]">Filters</h3>
              <button onClick={() => setShowFilters(false)} className="text-[#888]">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-8">
              {/* Categories */}
              <div>
                <h4 className="text-sm label-text text-[#888] mb-4">Category</h4>
                <div className="space-y-3">
                  {["", "clothing", "accessories", "apparel"].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setFilters(prev => ({ ...prev, category: cat }))}
                      className={`block w-full text-left py-2 px-3 border-l-2 transition-colors ${
                        filters.category === cat
                          ? "border-[#39FF14] text-[#39FF14] bg-[#39FF14]/5"
                          : "border-transparent text-[#888] hover:text-[#EDEDED] hover:border-[#333]"
                      }`}
                      data-testid={`filter-category-${cat || "all"}`}
                    >
                      {cat === "" ? "All Products" : categoryTitle[cat]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filters */}
              <div>
                <h4 className="text-sm label-text text-[#888] mb-4">Filter</h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="new-only"
                      checked={filters.newOnly}
                      onCheckedChange={(checked) => setFilters(prev => ({ ...prev, newOnly: checked }))}
                      className="border-[#333] data-[state=checked]:bg-[#39FF14] data-[state=checked]:border-[#39FF14]"
                      data-testid="filter-new"
                    />
                    <label htmlFor="new-only" className="text-[#EDEDED] cursor-pointer">
                      New Arrivals Only
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="featured-only"
                      checked={filters.featuredOnly}
                      onCheckedChange={(checked) => setFilters(prev => ({ ...prev, featuredOnly: checked }))}
                      className="border-[#333] data-[state=checked]:bg-[#39FF14] data-[state=checked]:border-[#39FF14]"
                      data-testid="filter-featured"
                    />
                    <label htmlFor="featured-only" className="text-[#EDEDED] cursor-pointer">
                      Featured Only
                    </label>
                  </div>
                </div>
              </div>

              {/* Clear Filters */}
              {(filters.category || filters.newOnly || filters.featuredOnly) && (
                <button
                  onClick={() => setFilters({ category: "", newOnly: false, featuredOnly: false, sort: "newest" })}
                  className="text-[#FF00FF] label-text hover:underline"
                  data-testid="clear-filters"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="aspect-[4/5] skeleton rounded-lg"></div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-24">
                <p className="text-[#888] text-xl">No products found</p>
                <button
                  onClick={() => setFilters({ category: "", newOnly: false, featuredOnly: false, sort: "newest" })}
                  className="mt-4 text-[#39FF14] label-text hover:underline"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
                {products.map((product) => (
                  <div key={product.id} className="animate-fade-in opacity-0">
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Overlay */}
      {showFilters && (
        <div
          className="fixed inset-0 bg-black/60 z-30 md:hidden"
          onClick={() => setShowFilters(false)}
        />
      )}
    </div>
  );
}
